import { formatBytes } from "../storage";
import { FORMAT_LIMIT_COPY } from "./format-limit.copy";
import type { CountLabel } from "./format-limit.types";
import type { ResolvedFeature } from "./entitlement.schema";

const HOURS_IN_DAY = 24;

/**
 * O intervalo de coleta é gravado em horas porque os planos pagos se separam
 * em frações de dia. Mas ler "72h" não diz nada: a partir de dois dias
 * fechados, a leitura passa para dias. Mesma regra do admin
 * (`apps/front-administrator/src/lib/entitlements/format-limit.ts`, `describeHours`).
 *
 * O limiar aqui é diferente do de `formatBytes` de propósito: "1 GB" já se lê
 * bem sozinho, mas "1 dia" no lugar de "24h" não economiza leitura — por isso
 * horas só vira dias a partir de 2 dias fechados (`>= 48h`), não de 1.
 */
function formatHours(stored: number): string {
  if (stored >= HOURS_IN_DAY * 2 && stored % HOURS_IN_DAY === 0) {
    return FORMAT_LIMIT_COPY.hoursAsDays(stored / HOURS_IN_DAY);
  }
  return FORMAT_LIMIT_COPY.hours(stored);
}

/** Retenção em dias. O admin nunca singulariza esse rótulo; mantido igual. */
function formatDays(stored: number): string {
  return FORMAT_LIMIT_COPY.days(stored);
}

/**
 * A janela de teste não existe no admin (chega no catálogo só na Task 9 deste
 * plano). Aqui o rótulo singulariza porque "1 dias" soa errado justamente onde
 * o número mais aparece: trial curto.
 */
function formatTrialDays(stored: number): string {
  return stored === 1 ? FORMAT_LIMIT_COPY.trialDay : FORMAT_LIMIT_COPY.trialDays(stored);
}

/**
 * Traduz um limite resolvido no rótulo que vai no chip e na célula da matriz.
 *
 * Portado de `apps/front-administrator/src/lib/entitlements/format-limit.ts`,
 * onde os casos difíceis de horas e dias já estavam resolvidos. Diverge do
 * admin em três pontos, porque a vitrine não tem os mesmos dados nem o mesmo
 * layout:
 * - o admin recebe `inputUnit` (ex.: "MB") vindo do catálogo e delega a
 *   conversão de bytes para quem edita o valor; a vitrine só lê, não edita, e
 *   `ResolvedFeature`/`unit` não carregam `inputUnit` — por isso bytes vira
 *   MB/GB aqui dentro, em vez de reusar `toInputValue`.
 * - o admin nunca embute o estado "desligado" nem o "gate ligado sem
 *   contagem" dentro do formatador: quem chama já sabe se a feature está
 *   ligada. Aqui os dois entram, porque o card/matriz só têm um
 *   `ResolvedFeature` para decidir tudo.
 * - o admin nunca produz sufixo por modo (`/mês`, `no total`); lá isso é
 *   resolvido fora do formatador, via `MODE_LABELS`. Aqui os sufixos entram
 *   porque o card/matriz não têm outro lugar pra decidir isso.
 */
export function formatLimit(
  feature: ResolvedFeature,
  unit: string | null,
  countLabel?: CountLabel,
): string {
  // O regime de teste vence o normal na vitrine, e por isso vem **antes** da
  // guarda de desligado: a feature está desligada fora do teste, e é
  // exatamente isso que a página de preços precisa sobrescrever. Sem este
  // ramo, um plano com 30 dias de assistente anuncia "—".
  //
  // O rótulo diz "no teste" de propósito: quem lê precisa saber que tem prazo.
  if (feature.trial?.enabled) {
    return feature.trial.limit === null
      ? FORMAT_LIMIT_COPY.trialOnly
      : FORMAT_LIMIT_COPY.trialWithLimit(feature.trial.limit);
  }

  if (!feature.enabled) return FORMAT_LIMIT_COPY.disabled;

  // Sem teto: gate puro ligado e janela de teste sem prazo são "incluso";
  // feature com contagem e sem teto é "ilimitado". `null` é ilimitado; `0` é
  // teto real — a comparação é explícita de propósito.
  if (feature.limit === null) {
    return feature.mode === null || feature.mode === "trialDays"
      ? FORMAT_LIMIT_COPY.included
      : FORMAT_LIMIT_COPY.unlimited;
  }

  if (feature.mode === "trialDays") return formatTrialDays(feature.limit);
  if (unit === "bytes") return formatBytes(feature.limit);

  // O prefixo vem do MODO, não da unidade. "a cada 15 dias" está certo para o
  // intervalo de coleta; "a cada 90 dias" ficaria errado em histórico
  // financeiro, que é retenção e também sai em dias.
  if (feature.mode === "interval") {
    return FORMAT_LIMIT_COPY.interval(
      unit === "hour" ? formatHours(feature.limit) : formatDays(feature.limit),
    );
  }

  if (unit === "hour") return formatHours(feature.limit);
  if (unit === "day") return formatDays(feature.limit);

  // O rótulo entra antes do sufixo de modo, para sair "120 min /mês" e não
  // "120 /mês min". Fica depois dos ramos de bytes/hora/dia de propósito:
  // aqueles já carregam a própria unidade no texto.
  const counted = countLabel
    ? `${feature.limit} ${feature.limit === 1 ? countLabel.one : countLabel.other}`
    : `${feature.limit}`;

  if (feature.mode === "monthly") return FORMAT_LIMIT_COPY.monthly(counted);
  if (feature.mode === "total") return FORMAT_LIMIT_COPY.total(counted);

  return counted;
}
